import React, { useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { User } from 'Shared/constants/types';

// Hooks
import useWindowDimensions from 'Wpds/hooks/useWindowDimensions';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { trackUserProfilePageClick } from 'Shared/tracking/tracking';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import WpPopover, { WpPopoverBody } from 'Wpds/components/popover-v1/WpPopover';
import WpAlert from 'Wpds/components/alert/WpAlert';
import WpAvatarGroup from 'Wpds/components/avatar/WpAvatarGroup';
import UserBgImage from 'Dashboard/components/UserProfilePage/ProfileContents/UserBgImage';
import ReferencePatternIcon from 'Shared/components/ReferencePatternIcon/ReferencePatternIcon';

// Styles
import './UserProfilePopover.scss';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  children: React.ReactNode;
  className?: string;
  userSlug?: string;
  user?: User;
};

const UserProfilePopover = ({ children, className, userSlug, user }: Props) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const [hasError, setHasError] = useState(false);
  const { width } = useWindowDimensions();
  const { featureFlagShowBaInfoInUserProfile: ffShowBa } = useFlags();

  const slug = user ? user.slug : userSlug;
  const { user: currentUser, isPiUser } = useCurrentUser();

  const userToRender = useSelector(state => UsersSelectors.getUserByUserSlug(state, { userSlug: slug }))?.toJS();
  const isLoading = !userToRender;
  const isMobile = width < 782;

  const { referencePatternName } = useBaProfile({ user: userToRender, withFetch: false });

  const fetchUserDetails = useCallback(() => {
    if (!userToRender)
      dispatch(UsersActions.fetchUserBySlug(slug))
        .then(response => {
          if (!response) setHasError(true);
        })
        .catch(() => {
          setHasError(true);
        });
  }, [userToRender]);

  if (!slug || isMobile) return <div className={className}>{children}</div>;

  const baseClassName = 'UserProfilePopover';

  const UserContentMarkUp = () => {
    const { id, avatar, fullName, title, department, slug } = userToRender;
    const userForAvatar = [
      { id, avatar: { color: avatar.color, initials: avatar.initials, profilePictureUrl: avatar.profilePictureUrl } },
    ];

    const profileUrl = PageUrlsUtils.makeUserProfilePageUrl(slug);
    const showTitle = title || department;

    return (
      <div className={`${baseClassName}__content`}>
        <WpAvatarGroup users={userForAvatar} size="xl" />
        {referencePatternName && ffShowBa && isPiUser ? (
          <div className={`${baseClassName}__content__reference-pattern`}>
            <ReferencePatternIcon
              referencePattern={referencePatternName}
              className={`${baseClassName}__content__reference-pattern__icon`}
            />
            <div className={`${baseClassName}__content__reference-pattern__details`}>
              <h5 className={`${baseClassName}__content__name`}>{fullName}</h5>
              <p className={`${baseClassName}__content__reference-pattern__name`}>{referencePatternName}</p>
            </div>
          </div>
        ) : (
          <h5 className={`${baseClassName}__content__name`}>{fullName}</h5>
        )}
        {showTitle && (
          <p className={`${baseClassName}__content__title`}>
            {title && <span className={`${baseClassName}__content__title__meta`}>{title}</span>}
            {department && <span className={`${baseClassName}__content__title__meta`}>{department}</span>}
          </p>
        )}
        <Link
          to={profileUrl}
          className={`${baseClassName}__content__profile-link`}
          onClick={() => trackUserProfilePageClick(currentUser, slug)}
        >
          View profile
        </Link>
      </div>
    );
  };

  return (
    <>
      <div className={className} ref={ref}>
        {children}
      </div>
      <WpPopover target={ref} onShow={fetchUserDetails} size="lg">
        <WpPopoverBody>
          {hasError ? (
            <WpAlert type="note">Error fetching user details</WpAlert>
          ) : (
            <div className={baseClassName}>
              {isLoading ? (
                <div>Loading User...</div>
              ) : (
                <div>
                  <UserBgImage size="sm" user={userToRender} />
                  <UserContentMarkUp />
                </div>
              )}
            </div>
          )}
        </WpPopoverBody>
      </WpPopover>
    </>
  );
};

export default UserProfilePopover;
